import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { decryptId } from "../../../modules/auth/core/crypto";
// Centralized query key
import * as api from "../api/user.api";
import type {
	ChangePasswordPayload,
	RoleList,
	UsersPayload,
} from "../types/users.type";
export const USERS_QUERY_KEY = "userData";
export const CHANGEPASSWORD_QUERY_KEY = ["changePassword"];
export const ROLES_QUERY_KEY = ["roles"];
export const AUTH_QUERY_KEY = ["auth"];
export const useUsers = (roleId: string, page = 1, limit = 10, search = "") => {
	const [_decryptedRoleId, setDecryptedRoleId] = useState<number | null>(null);

	// Decrypt once when the inputs change
	useEffect(() => {
		const decryptIds = async () => {
			try {
				if (roleId?.length > 0) {
					const role = await decryptId(roleId);
					setDecryptedRoleId(Number(role));
				} else {
					setDecryptedRoleId(null);
				}
			} catch (error) {
				console.error("Decryption failed:", error);
				setDecryptedRoleId(null);
			}
		};

		decryptIds();
	}, [roleId]);

	// Fetch users after decryption
	const query = useQuery<any, Error>({
		queryKey: [USERS_QUERY_KEY, roleId, page, limit, search],
		queryFn: () => api.fetchUsers(roleId, page, limit, search),
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		user: query.data?.items ?? [],
		totalItems: query.data?.totalItems ?? 0,
		totalPages: query.data?.totalPages ?? 0,
	};
};

// ✅ Create a users
export const useCreateUsers = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (payload: UsersPayload) => api.createUsers(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
		},
		onError: (error: any) => {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to create user";
			toast.error(message);
		},
	});

	return mutation;
};
export const useRefreshToken = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: () => api.refreshToken(),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
		},
		onError: () => {
			console.error("Token refresh failed");
		},
	});
	return mutation;
};

// ✅ Update a Users
export const useUpdateUsers = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: ({ id, payload }: { id: number; payload: UsersPayload }) =>
			api.updateUsers(id, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
			queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
		},
		onError: (error: any) => {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to update user";
			toast.error(message);
		},
	});

	return mutation;
};

// ✅ Delete a User
export const useDeleteUser = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (id: number) => api.deleteUser(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
			toast.success("User deleted successfully");
		},
		onError: (error: any) => {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to delete user";
			toast.error(message);
		},
	});

	return mutation;
};

// ✅ Fetch all Roles
export const useRolesList = (show?: boolean) => {
	const query = useQuery<RoleList[], Error>({
		queryKey: ROLES_QUERY_KEY,
		queryFn: api.fetchRoleList,
		enabled: show,
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		user: query.data ?? [],
	};
};

export const useUserGetByIdList = (id: number | undefined) => {
	return useQuery<UsersPayload, Error>({
		queryKey: [USERS_QUERY_KEY, id],
		queryFn: () => api.usersGetById(id),
		enabled: !!id,
		retry: 2,
	});
};

// ✅ Create a CHANGE PASSWORD
export const useChangePassword = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: (payload: ChangePasswordPayload) => api.changePassword(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CHANGEPASSWORD_QUERY_KEY });
			toast.success("Password changed successfully");
		},
		onError: (error: any) => {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to change password";
			toast.error(message);
		},
	});

	return mutation;
};
